import { Component, type ErrorInfo, type ReactNode } from 'react';
import Button from './Button';
import ContactLine from './ContactLine';
import Eyebrow from './Eyebrow';

interface Props {
  children: ReactNode;
}

interface State {
  error: Error | null;
}

/**
 * Last-resort net under the whole router. A throw during render unmounts the
 * entire tree, so without this a client sees a white page and has no way to
 * tell whether the link is broken, their edits are gone, or Henry needs to
 * know. Individual risky paths (document parsing) guard themselves first —
 * this catches whatever slips past them.
 *
 * Recovery is a reload rather than a state reset: whatever threw is almost
 * always the data the page just loaded, so re-rendering the same props would
 * throw straight back into this fallback.
 */
export default class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('Unhandled render error', error, info.componentStack);
  }

  render() {
    const { error } = this.state;
    if (!error) return this.props.children;

    return (
      <main className="min-h-screen bg-page text-fg flex items-center justify-center px-6">
        <div className="text-center max-w-md">
          <Eyebrow accent>// SOMETHING WENT WRONG</Eyebrow>
          <h1 className="font-display font-bold text-[40px] leading-[1.05] tracking-[-0.02em] mt-3 mb-4">
            This page stopped responding
          </h1>
          <p className="text-muted leading-[1.55]">
            Something broke while displaying it. Reloading usually clears it, and anything
            already saved is unaffected.
          </p>
          <div className="mt-6">
            <Button variant="primary" glow onClick={() => window.location.reload()}>
              Reload the page
            </Button>
          </div>
          <ContactLine className="mt-6" />
          <p className="font-mono text-[10px] uppercase tracking-[0.12em] text-faint mt-6 select-all">
            {error.message}
          </p>
        </div>
      </main>
    );
  }
}
